import { useState } from "react";
import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import { defaultP4FormData, type P4FormData, type P4Question } from "./p4Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

const DIM_VARIANT: Record<P4Question["dimension"], "default" | "secondary" | "outline"> = {
    economy: "default",
    efficiency: "secondary",
    effectiveness: "outline",
};

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: import("@/app/lib/auditflowApi").LauraStageState | null) => void;
};

export function LauraP4Workspace(props: Props) {
    const { t } = useTranslation();
    const [tab, setTab] = useState<string>("summary");

    return (
        <LauraStageShell<P4FormData>
            audit={props.audit}
            stageId="p4"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP4FormData}
            title={t("laura", "p4Title")}
            description="Audit design matrix — questions, procedures, and risk traceability"
            generateLabel={t("laura", "generateMatrix")}
        >
            {({ form, setForm }) => (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap gap-1">
                        {form.questions.map((q) => (
                            <Button
                                key={q.code}
                                type="button"
                                size="sm"
                                variant={tab === q.code ? "default" : "outline"}
                                onClick={() => setTab(q.code)}
                            >
                                <Badge variant={DIM_VARIANT[q.dimension]} className="mr-1">
                                    {q.dimension}
                                </Badge>
                                {q.code}
                            </Button>
                        ))}
                        <Button
                            type="button"
                            size="sm"
                            variant={tab === "summary" ? "default" : "outline"}
                            onClick={() => setTab("summary")}
                        >
                            Summary
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                const n = form.questions.length + 1;
                                setForm((p) => ({
                                    ...p,
                                    questions: [
                                        ...p.questions,
                                        {
                                            code: `QST-${String(n).padStart(2, "0")}`,
                                            title: "",
                                            text: "",
                                            dimension: "effectiveness",
                                            linked_risks: [],
                                            sub_questions: [],
                                            rows: [],
                                        },
                                    ],
                                }));
                            }}
                        >
                            + QST
                        </Button>
                    </div>

                    <Field>
                        <FieldLabel>Fundamental question</FieldLabel>
                        <Textarea
                            rows={2}
                            value={form.fundamental_question}
                            onChange={(e) =>
                                setForm((p) => ({
                                    ...p,
                                    fundamental_question: e.target.value,
                                }))
                            }
                        />
                    </Field>

                    {tab === "summary" ? (
                        <>
                            <AdmSummary questions={form.questions} />
                            <TraceabilityMatrix questions={form.questions} />
                            <ConsistencyGate
                                checks={form.consistency_checks}
                                onChange={(checks) =>
                                    setForm((p) => ({ ...p, consistency_checks: checks }))
                                }
                            />
                        </>
                    ) : (
                        <QuestionEditor
                            question={form.questions.find((q) => q.code === tab)!}
                            onChange={(q) =>
                                setForm((p) => ({
                                    ...p,
                                    questions: p.questions.map((x) =>
                                        x.code === q.code ? q : x,
                                    ),
                                }))
                            }
                        />
                    )}
                </div>
            )}
        </LauraStageShell>
    );
}

function QuestionEditor({
    question,
    onChange,
}: {
    question: P4Question | undefined;
    onChange: (q: P4Question) => void;
}) {
    if (!question) return null;
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">{question.code}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <FieldGroup>
                    <Field>
                        <FieldLabel>Title</FieldLabel>
                        <Input
                            value={question.title}
                            onChange={(e) =>
                                onChange({ ...question, title: e.target.value })
                            }
                        />
                    </Field>
                    <Field>
                        <FieldLabel>Question text</FieldLabel>
                        <Textarea
                            rows={2}
                            value={question.text}
                            onChange={(e) =>
                                onChange({ ...question, text: e.target.value })
                            }
                        />
                    </Field>
                </FieldGroup>
                <Table>
                    <TableHeader>
                        <TableRow>
                            {[
                                "Required Info",
                                "Sources",
                                "Criteria",
                                "Procedures",
                                "Evidence",
                                "Findings",
                            ].map((h) => (
                                <TableHead key={h}>{h}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {question.rows.map((row, i) => (
                            <TableRow key={i}>
                                {(Object.keys(row) as (keyof typeof row)[]).map((k) => (
                                    <TableCell key={k} className="p-1">
                                        <Input
                                            className="h-8 text-xs"
                                            value={row[k]}
                                            onChange={(e) => {
                                                const rows = [...question.rows];
                                                rows[i] = { ...row, [k]: e.target.value };
                                                onChange({ ...question, rows });
                                            }}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                        onChange({
                            ...question,
                            rows: [
                                ...question.rows,
                                {
                                    required_info: "",
                                    sources: "",
                                    criteria: "",
                                    procedures: "",
                                    possible_evidence: "",
                                    possible_findings: "",
                                },
                            ],
                        })
                    }
                >
                    + row
                </Button>
            </CardContent>
        </Card>
    );
}

function AdmSummary({ questions }: { questions: P4Question[] }) {
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">ADM summary</CardTitle>
            </CardHeader>
            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>QST</TableHead>
                            <TableHead>Risks</TableHead>
                            <TableHead>Rows</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {questions.map((q) => (
                            <TableRow key={q.code}>
                                <TableCell className="font-mono">{q.code}</TableCell>
                                <TableCell>{q.linked_risks.join(", ")}</TableCell>
                                <TableCell>{q.rows.length}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function TraceabilityMatrix({ questions }: { questions: P4Question[] }) {
    const allRisks = [...new Set(questions.flatMap((q) => q.linked_risks))].sort();
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">Risk → question traceability</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>RIS</TableHead>
                            {questions.map((q) => (
                                <TableHead key={q.code}>{q.code}</TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {allRisks.map((ris) => (
                            <TableRow key={ris}>
                                <TableCell className="font-mono">{ris}</TableCell>
                                {questions.map((q) => (
                                    <TableCell
                                        key={q.code}
                                        className={cn(
                                            "text-center",
                                            q.linked_risks.includes(ris) &&
                                                "bg-primary/10 font-medium",
                                        )}
                                    >
                                        {q.linked_risks.includes(ris) ? "✓" : ""}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function ConsistencyGate({
    checks,
    onChange,
}: {
    checks: P4FormData["consistency_checks"];
    onChange: (c: P4FormData["consistency_checks"]) => void;
}) {
    const allDone = checks.every((c) => c.checked);
    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-sm">ADM consistency gate</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                <ul className="flex flex-col gap-2">
                    {checks.map((c, i) => (
                        <li key={c.id}>
                            <label className="flex items-start gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={c.checked}
                                    onChange={(e) => {
                                        const next = [...checks];
                                        next[i] = { ...c, checked: e.target.checked };
                                        onChange(next);
                                    }}
                                />
                                <span>{c.text}</span>
                            </label>
                        </li>
                    ))}
                </ul>
                {!allDone && (
                    <Alert variant="destructive">
                        <AlertTitle>Gate open</AlertTitle>
                        <AlertDescription>
                            Resolve all checks before proceeding to P5.
                        </AlertDescription>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
}
